import { html, nothing } from 'lit';
import { series } from '../../types.js';
import { ValueFormatter } from '../../../../visualizations/common/format.js';
import { Legend } from '../../legend/Legend.js';
import { mapKeyLegendData } from '../../legend/mapLegendData.js';
import { getLegendsConfiguration } from '../../legend/getLegendsConfiguration.js';
import { styleMap } from 'lit/directives/style-map.js';
import { legendKeyData } from '../../legend/types.js';
import { register as _registerElement } from "../../../../elements/one-ux-popout/register-element.js";
_registerElement("popout-e774f495b37dd905168219b2d220d908");
const legendsConfiguration = getLegendsConfiguration({
  isPopout: true
});
export function KeyHoverPopout(referenceElement: Element, series: series[], valueFormatter: ValueFormatter) {
  const key = (referenceElement as HTMLElement).dataset.key;
  if (!key) {
    return nothing;
  }
  const legendData = getSortedLegendData(series, key);
  if (!legendData.length) {
    return nothing;
  }
  const {
    legendDataLeftYTarget,
    legendDataRightYTarget
  } = getGroupedLegendDataOnYTarget(legendData);
  const hasYTargetLeftAndRight = legendDataLeftYTarget.length && legendDataRightYTarget.length;
  const {
    legendsVerticalGap,
    legendsHorizontalGap
  } = legendsConfiguration;
  const LegendFragment = (data: legendKeyData) => Legend({
    legendsConfiguration,
    type: data.type,
    color: data.color,
    name: data.name,
    value: valueFormatter.formatValue(Number(data.value), false),
    highlightStateProvider: () => 'none'
  });
  return html`
    <popout-e774f495b37dd905168219b2d220d908
      aria-hidden="true"
      style="pointer-events: none; color: var(--one-ux-palette--brand-neutral-600);"
      .reference=${referenceElement}
      direction="horizontal"
      alignment="center"
      .offsetReference=${-referenceElement.clientWidth / 2 + 8}
    >
      <div
        style=${styleMap({
    display: 'flex',
    'flex-flow': 'column nowrap',
    gap: `${legendsVerticalGap}px ${legendsHorizontalGap}px`
  })}
      >
        ${[...legendDataLeftYTarget.map(data => LegendFragment(data)), hasYTargetLeftAndRight ? html`<div style="border-bottom: 1px solid var(--one-ux-palette--brand-neutral-200)"></div>` : nothing, ...legendDataRightYTarget.map(data => LegendFragment(data))]}
      </div>
    </popout-e774f495b37dd905168219b2d220d908>
  `;
}
const getGroupedLegendDataOnYTarget = (legendData: legendKeyData[]) => {
  const {
    left,
    right
  } = legendData.reduce<{
    left: legendKeyData[];
    right: legendKeyData[];
  }>((acc, entry) => {
    acc[entry.yTarget].push(entry);
    return acc;
  }, {
    left: [],
    right: []
  });
  return {
    legendDataLeftYTarget: left,
    legendDataRightYTarget: right
  };
};
const getSortedLegendData = (series: series[], key: string) => {
  return mapKeyLegendData(series, key).map((data, index) => ({
    index,
    data
  })).sort((a, b) => {
    const isColumnStackedA = a.data.type === 'column-stacked';
    const isColumnStackedB = b.data.type === 'column-stacked';
    if (!isColumnStackedA && isColumnStackedB) return 1;
    if (isColumnStackedA && !isColumnStackedB) return -1;
    if (isColumnStackedA && isColumnStackedB) {
      const valueA = Number(a.data.value);
      const valueB = Number(b.data.value);
      if (valueA >= 0 && valueB >= 0) return b.index - a.index;
      if (valueA >= 0 && valueB < 0) return -1;
      if (valueA < 0 && valueB >= 0) return 1;
    }
    return a.index - b.index;
  }).map(x => x.data);
};